/**
 * Supabase project config. Publishable key + URL come from env vars (`VITE_*`).
 *
 * 2026-08-23: migrated off the legacy `anon` JWT key — it was disabled on the
 * shared Sentinel project on 2026-06-13, which broke Realtime and every direct
 * PostgREST call with "Legacy API keys are disabled". The `sb_publishable_...`
 * key is its replacement (same low privileges, RLS unchanged) and is safe to
 * ship in the bundle. NOTE: publishable keys are NOT JWTs — they go in the
 * `apikey` header, never in `Authorization: Bearer` (see lib/api.ts).
 *
 * We don't use the supabase-js client for edge function calls — every Station
 * edge function call goes through `lib/api.ts` so we control headers, retries,
 * and error shape consistently. The client below is for Realtime channels and
 * the admin pages' direct PostgREST reads only.
 */

import { createClient } from "@supabase/supabase-js";
import { getStoredToken } from "./auth";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

if (!url || !publishableKey) {
  // eslint-disable-next-line no-console
  console.error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY env vars. " +
      "Copy .env.example → .env and fill them in (the key is the sb_publishable_... " +
      "key from Supabase → Settings → API Keys, NOT the disabled legacy anon JWT)."
  );
}

export const SUPABASE_URL = url || "";
export const SUPABASE_PUBLISHABLE_KEY = publishableKey || "";
export const FUNCTIONS_BASE = `${SUPABASE_URL}/functions/v1`;

/**
 * Shared client for Realtime channels and the admin pages' direct PostgREST
 * reads/RPCs. The custom fetch injects the CURRENT session token on every
 * request (read lazily — the token changes at login/logout, so it can't be a
 * static `global.headers` entry). Server-side staff gates and RLS policies
 * authorize via that `x-customer-token` header (PostgREST request.headers
 * GUC) — without it, staff-gated RPCs like get_comms_queue raise Unauthorized
 * even for logged-in staff.
 */
export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  global: {
    fetch: (input, init) => {
      const token = getStoredToken();
      if (!token) return fetch(input, init);
      const headers = new Headers(init?.headers);
      headers.set("x-customer-token", token);
      return fetch(input, { ...init, headers });
    },
  },
});
